import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import AdminTable from '../../components/CommonComponents/AdminTable'
import Response_Processing from '../../layout/Others/Response_Processing'
import AdminDeleteModal from '../../components/CommonComponents/AdminDeleteModal'
import AdminNavbar from '../../components/CommonComponents/AdminNavbar'
import AdminCustomButton from '../../components/CommonComponents/AdminCustomButton'
import AdminTooltip from '../../components/CommonComponents/AdminTooltip'
import ExportIcon from '../../components/Images/icons/Export_Icon_01.png'
import { BiRecycle } from 'react-icons/bi'
import { RxReload } from 'react-icons/rx'
import { getItemToken } from '../../Utils/localStorageUtils'
import MessageBox from '../../components/CommonComponents/MessageBox'
import { FaEllipsisVertical } from "react-icons/fa6";
import { LiaUndoAltSolid } from "react-icons/lia";
import { IoClose } from 'react-icons/io5'
import AdminMiniPlayer from '../../components/CommonComponents/AdminMiniPlayer'
import AdminCustomURLCopy from '../../components/CommonComponents/AdminCustomURLCopy'


export default function UGCIndex() {
  // table + ui state
  const [apiData, setApiData] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('') // '', '0','1','2'
  const [reason, setReason] = useState('')
  const [rejectId, setRejectId] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [itemToDelete, setItemToDelete] = useState([])
  const [draft, setDraft] = useState(0) // 0 = active list, 1 = recycle bin
  const [deleteActionType, setDeleteActionType] = useState(2)
  const [withOutRefresh, setWithoutRefresh] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)
  const [modelOpen, setModelOpen] = useState(false)
  const [formData, setFormData] = useState({ title: '' })
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewEmbed, setPreviewEmbed] = useState(null);
  const [previewOptions, setPreviewOptions] = useState({ sources: [{ src: '', type: '' }] });

  const headers = getItemToken('access_token')
  const BaseUrl = process.env.REACT_APP_Baseurl
  const listApi = `${BaseUrl}/admin/ugc`
  const multiDelete = `${BaseUrl}/admin/videos/multi-delete` // reuse

  // fetch UGC list (supports status, soft_delete, search client-side)
  async function fetchData() {
    setLoading(true)
    try {
      const { data } = await axios.get(listApi, {
        headers,
        params: { status: status !== '' ? status : undefined, soft_delete: draft }
      })
      const rows = data?.submissions || []
      setApiData(rows)
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [status, draft, withOutRefresh])

  // approve / reject endpoints
  async function approve(id) {
    try {
      await axios.post(`${BaseUrl}/admin/ugc/${id}/approve`, {}, { headers })
      toast.success('Approved')
      fetchData()
    } catch (e) { toast.error(e?.response?.data?.message || e.message) }
  }
  async function reject(id, reason) {
    try {
      await axios.post(`${BaseUrl}/admin/ugc/${id}/reject`, { reason }, { headers });
      setRejectId(null);
      setRejectReason(''); // reset state
      toast.success('Rejected');
      fetchData();
    } catch (e) {
      const res = e?.response?.data;
      if (res?.errors && Array.isArray(res.errors) && res.errors.length > 0) {
        toast.error(res.errors[0].msg);
      } else if (res?.message) {
        toast.error(res.message);
      } else {
        toast.error('Something went wrong, please try again.');
      }
    }
  }


  // multi delete/undo/permanent via existing multi-delete endpoint
  async function handleConfirmDelete(actionType) {
    try {
      setShowOverlay(true)
      await axios.delete(multiDelete, {
        headers,
        data: { id: itemToDelete, action: actionType || deleteActionType }
      })
      toast.success(actionType === 1 ? 'Permanently deleted' : actionType === 3 ? 'Restored' : 'Moved to Recycle Bin')
      setItemToDelete([])
      setWithoutRefresh(prev => !prev)
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message)
    } finally { setShowOverlay(false); setModelOpen(false) }
  }

  const closeModal = () => { setModelOpen(false); setItemToDelete([]); setDeleteActionType(2) }
  const openModal = () => { setModelOpen(true) }
  const singleDelete = (id) => { setItemToDelete([id]); setDeleteActionType(draft === 1 ? 3 : 2); setModelOpen(true) }
  const singlePermanentDelete = (id) => { setItemToDelete([id]); setDeleteActionType(1); setModelOpen(true) }

  // search, client-side filter
  const searchTerm = (formData?.title || '').trim().toLowerCase()
  const labels = ['Uploaded', 'Queued', 'Processing', 'Done', 'Failed']
  const getStatusLabel = (row) => row?.ugc_status === 1 ? 'Approved' : row?.ugc_status === 2 ? 'Rejected' : 'Pending'
  const getTransLabel = (row) => labels[row?.transcoding_status ?? 0]

  const filteredRows = useMemo(() => {
    if (!searchTerm) return apiData
    return apiData.filter(r => {
      const title = (r?.title || '').toString().toLowerCase()
      const statusText = getStatusLabel(r).toLowerCase()
      const transText = getTransLabel(r).toLowerCase()
      return [title, statusText, transText].some(v => v.includes(searchTerm))
    })
  }, [apiData, searchTerm])

  // pagination (client-side)
  const totalItems = filteredRows.length
  const total = { totalItems, currentPage: page, count_all: totalItems, totalPages: Math.max(1, Math.ceil(totalItems / (limit || 1))) }
  const pagedRows = useMemo(() => {
    const start = (page - 1) * limit
    return filteredRows.slice(start, start + limit)
  }, [filteredRows, page, limit])

  const options = {
    pageStartIndex: 1,
    paginationSize: 3,
    sizePerPage: limit,
    sizePerPageList: [
      { text: '5', value: 5 },
      { text: '10', value: 10 },
      { text: 'All', value: totalItems }
    ]
  }

  const handlePageChange = (p) => setPage(p)
  const handleSizePerPageChange = (e) => { setLimit(Number(e.target.value)); setPage(1) }
  const handleRowSelect = (rowId) => {
    setItemToDelete(prev => prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId])
  }
  const handleSelectAll = () => {
    if (itemToDelete.length === pagedRows.length) setItemToDelete([])
    else setItemToDelete(pagedRows.map(r => r.id))
  }
  const handleSort = (field) => {
    const sorted = [...apiData].sort((a, b) => {
      const av = a?.[field]; const bv = b?.[field]
      if (av === bv) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return String(av || "").localeCompare(String(bv || ""))
    })
    setApiData(sorted)
    setPage(1)
  }

  const handleInput = (e) => {
    const { type, name, value } = e.target
    if (type === 'search') { setFormData(prev => ({ ...prev, [name]: value })); setPage(1) }
  }

  // toolbar actions
  const reloadAction = () => { setDraft(0); setWithoutRefresh(prev => !prev) }
  const reCycleBin = () => { setDraft(prev => prev === 1 ? 0 : 1); setItemToDelete([]) }

  async function fetchAllData(type, find) {
    // get full list from api (no pagination in backend)
    try {
      const { data } = await axios.get(listApi, { headers, params: { status: status !== '' ? status : undefined, soft_delete: draft } })
      let updatedList = data?.submissions || []

      if (find !== 'ALL') {
        if (!itemToDelete || itemToDelete.length === 0) { alert('Please select at least one row to export.'); return }
        updatedList = updatedList.filter(item => itemToDelete.includes(item?.id))
        if (!updatedList || updatedList.length === 0) { alert('Selected data not found.'); return }
      }

      const exportData = updatedList.map((item, index) => ({
        'S.NO': index + 1,
        'TITLE': item?.title,
        'TRANSCODING STATUS': getTransLabel(item),
        'UGC STATUS': getStatusLabel(item),
        'UPLOADED BY': item?.uploader_name || '-',
        'UPLOADED ON': item?.upload_on || item?.createdAt,
      }))

      const { donwloadCSV, videodownloadPDF, videodownloadXLSX } = await import('../../components/CommonMethod/CommonMethods')
      if (type === 'CSV') await donwloadCSV(exportData, 'UGC-LIST')
      else if (type === 'PDF') await videodownloadPDF(exportData, 'UGC-LIST', ['S.NO', 'TITLE', 'TRANSCODING STATUS', 'UGC STATUS', 'UPLOADED BY'])
      else if (type === 'XLSX') await videodownloadXLSX(exportData, 'UGC-LIST', ['S.NO', 'TITLE', 'TRANSCODING STATUS', 'UGC STATUS', 'UPLOADED BY'])
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message)
    }
  }

  const handleApprove = (id) => {
    setApproveTarget(id);
    setApproveModalOpen(true);
  };

  const openPreview = (row) => {
    try {
      setPreviewRow(row);

      // Helpers to normalize embed sources
      const toEmbedUrl = (val) => {
        try {
          if (!val || typeof val !== 'string') return null;
          const s = val.trim();
          if (/^<iframe/i.test(s)) return null; // real iframe HTML handled outside
          if (/^https?:\/\//i.test(s)) {
            // YouTube
            const ytWatch = s.match(/^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^&]+)/i);
            const ytShort = s.match(/^https?:\/\/(?:www\.)?youtu\.be\/([^?&#]+)/i);
            if (ytWatch && ytWatch[1]) return `https://www.youtube.com/embed/${ytWatch[1]}`;
            if (ytShort && ytShort[1]) return `https://www.youtube.com/embed/${ytShort[1]}`;
            // Vimeo
            const vimeo = s.match(/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/i);
            if (vimeo && vimeo[1]) return `https://player.vimeo.com/video/${vimeo[1]}`;
            // Unknown URL, try to use as-is in iframe
            return s;
          }
          return null;
        } catch {
          return null;
        }
      };

      const buildIframe = (src) => (
        `<iframe src="${src}"
          style="width:100%;height:360px;border:0;border-radius:12px;"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
          loading="lazy"
        ></iframe>`
      );

      const m3u8 = (row?.m3u8_play_url || row?.m3u8_url || '').trim();
      const mp4 = (row?.mp4_play_url || row?.mp4_url || '').trim();
      const embedRaw = (row?.embed_code || '').trim();

      if (embedRaw && (!m3u8 && !mp4)) {
        if (/^<iframe/i.test(embedRaw)) {
          setPreviewEmbed(embedRaw);
          setPreviewOptions({ sources: [{ src: '', type: '' }] });
        } else {
          const eurl = toEmbedUrl(embedRaw);
          if (eurl) {
            setPreviewEmbed(buildIframe(eurl));
            setPreviewOptions({ sources: [{ src: '', type: '' }] });
          } else {
            setPreviewEmbed(null);
            setPreviewOptions({ sources: [{ src: '', type: '' }] });
          }
        }
      } else if (m3u8) {
        setPreviewEmbed(null);
        setPreviewOptions({ sources: [{ src: m3u8, type: 'application/x-mpegURL' }] });
      } else if (mp4) {
        setPreviewEmbed(null);
        setPreviewOptions({ sources: [{ src: mp4, type: 'video/mp4' }] });
      } else {
        setPreviewEmbed(null);
        setPreviewOptions({ sources: [{ src: '', type: '' }] });
      }

      setPreviewOpen(true);
    } catch (_) {
      setPreviewEmbed(null);
      setPreviewOptions({ sources: [{ src: '', type: '' }] });
      setPreviewOpen(true);
    }
  };


  // columns
  const columns = [
    {
      dataField: 'title',
      text: 'UGC',
      width: '30%',
      colStyle: 'px-5',
      formatter: (cell, row) => (
        <div className='d-flex justify-content-start align-items-center'>
          {row?.image_URL ? (
            <img src={row.image_URL} alt={row?.title || 'poster'} className='profile theme-bg-color-secondary' />
          ) : (
            <div className='profile theme-bg-color-secondary text-dark d-flex align-items-center justify-content-center admin-input-title'>
              {(row?.title || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className='rightVideoTitle ms-2'>
            <span className='theme-text-color table-heading d-flex align-items-center'>{row?.title || '-'}</span>
          </div>
        </div>
      ),
      sort: true,
    },
    {
      dataField: 'player_image_URL',
      text: 'Player',
      width: '12% ',
      formatter: (cell, row) => (
        row?.player_image_URL ? (
          <img src={row.player_image_URL} alt='player' style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 6 }} />
        ) : '-'
      )
    },
    {
      dataField: 'uploader_name',
      text: 'Uploaded By',
      width: '18%',
      sort: true,
      formatter: (cell, row) => (
        <div className='d-flex flex-column'>
          <span className='theme-text-color'>{row?.uploader_name || '-'}</span>
          {row?.uploader_email ? (
            <small className='text-muted'>{row.uploader_email}</small>
          ) : null}
        </div>
      )
    },
    {
      dataField: 'createdAt',
      text: 'Uploaded On',
      width: '15%',
      sort: true,
      formatter: (cell, row) => {
        if (!row?.createdAt) return '-';
        const date = new Date(row.createdAt);

        const month = date.toLocaleString('en-US', { month: 'long' }); // October
        const day = date.getDate(); // 9
        const year = date.getFullYear(); // 2025

        return `${month} ${day} ${year}`;
      },
    },

    {
      dataField: 'status',
      text: 'Status',
      width: '12%',
      formatter: (cell, row) => {
        const label = getStatusLabel(row);

        // Map ugc_status to a CSS class (old logic)
        let statusClass = '';
        if (row?.ugc_status === 1) statusClass = 'PUBLISHED_active';
        else if (row?.ugc_status === 2) statusClass = 'DISABLED_active';
        else statusClass = 'UNPUBLISHED_active';

        // Use new UI layout
        return (
          <div
            className={`d-inline-flex justify-content-center px-3 py-1 w-75 text-center ${statusClass}`}
          >
            <p style={{ fontSize: '10px', fontWeight: '700' }}>{label}</p>
          </div>
        );
      },
    },

    {
      dataField: 'ACTIONS',
      text: 'Actions',
      width: '28%',
      isDummyField: true,
      formatter: (cell, r) => {
        const isRejected = r?.ugc_status === 2;

        return (
          <div className="editdropdown">
            {/* Dropdown trigger */}
            <span className="editdropdown-button">
              <FaEllipsisVertical className="theme-text-color" />
            </span>

            {/* Dropdown menu */}
            <div className="editdropdown-menu theme-bg-color rounded-2 overflow-hidden">

              {/* ✅ If rejected, show reason box at top */}
              {isRejected && r?.ugc_reject_reason && (
                <div className="p-2 border-bottom text-danger small bg-light">
                  <strong>Reason:</strong> {r.ugc_reject_reason}
                </div>
              )}

              {/* ▶ Preview */}
              <div onClick={() => openPreview(r)} className="commonActionPadding theme-bg-color">
                <MessageBox text="Preview" classname="ms-2 theme-text-color" />
              </div>

              {/* ✅ Approve (show if draft = 0 and item is rejected OR pending) */}
              {draft === 0 && (isRejected || r?.ugc_status === 0) && (
                <div onClick={() => handleApprove(r.id)} className="commonActionPadding theme-bg-color" >
                  <MessageBox text="Approve" classname="ms-2 theme-text-color" />
                </div>
              )}

              {draft === 0 && !isRejected && (
                <div
                  onClick={() => {
                    setRejectTarget(r.id);
                    setRejectReason(''); // reset previous reason
                    setRejectModalOpen(true); // open modal
                  }}
                  className="commonActionPadding theme-bg-color"
                >
                  <MessageBox text="Reject" classname="ms-2 theme-text-color" />
                </div>

              )}


              {/* 🗑️ Delete / Undo */}
              {draft === 0 && (
                <div onClick={() => singleDelete(r.id)} className="commonActionPadding theme-bg-color">
                  <MessageBox text="Delete" classname="ms-2 theme-text-color" />
                </div>
              )}

              {draft === 1 && (
                <>
                  <div onClick={() => singleDelete(r.id)} className="commonActionPadding theme-bg-color">
                    <LiaUndoAltSolid style={{ width: "20px", height: "20px" }} />
                    <MessageBox text="Undo" classname="ms-2 theme-text-color" />
                  </div>
                  <div onClick={() => singlePermanentDelete(r.id)} className="commonActionPadding theme-bg-color">
                    <MessageBox text="Delete Permanent" classname="ms-2 theme-text-color" />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      }
    }

  ]


  return (
    <div className=''>

      {rejectModalOpen && (
        <div className={`modal packageModel fade d-block show`} tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content theme-bg-color">
              <div className="modal-header theme-border-bottom-secondary">
                <MessageBox text="Confirm Rejection" classname="modal-title theme-text-color admin-modal-title fw-bold" />
                <button
                  type="button"
                  className="bg-transparent theme-text-color"
                  onClick={() => setRejectModalOpen(false)}
                >
                  <IoClose fill="currentColor" style={{ width: "25px", height: "25px" }} />
                </button>
              </div>

              <div className="modal-body">
                <textarea
                  className="form-control"
                  placeholder="Enter reason for rejection"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </div>

              <div className="modal-footer border-0">
                <button
                  className="bg-transparent theme-text-color sliderBtnText px-2"
                  onClick={() => setRejectModalOpen(false)}
                >
                  <MessageBox text="Cancel" />
                </button>

                <AdminCustomButton
                  className="warningColorBg px-3 py-1 m-0"
                  saveMethod={() => {
                    if (!rejectReason.trim()) {
                      toast.error("Reason is required");
                      return;
                    }
                    reject(rejectTarget, rejectReason);
                    setRejectModalOpen(false);
                    setRejectReason('');
                  }}
                  saveText="Confirm"
                />
              </div>
            </div>
          </div>
        </div>
      )}




      {
        approveModalOpen && (
          <AdminDeleteModal
            headingText="Confirm Approval"
            message="Are you sure you want to approve this video?"
            modelClose={approveModalOpen}
            deleteClose={() => setApproveModalOpen(false)}
            deleteAction={() => {
              approve(approveTarget);
              setApproveModalOpen(false);
            }}
            buttonText="custom"
            actionText="Approve"
            buttonBg="danger" // ✅ triggers green btn-success in current modal logic
          />
        )
      }
      {showOverlay && <Response_Processing />}

      {/* Preview Modal */}
      {previewOpen && (
        <div className={`modal packageModel fade d-block show`} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content theme-bg-color">
              <div className="modal-header theme-border-bottom-secondary">
                <MessageBox text={previewRow?.title ? `Preview: ${previewRow.title}` : 'Preview'} classname="modal-title theme-text-color admin-modal-title fw-bold" />
                <button
                  type="button"
                  className="bg-transparent theme-text-color"
                  onClick={() => { setPreviewOpen(false); setPreviewRow(null); setPreviewEmbed(null); }}
                >
                  <IoClose fill="currentColor" style={{ width: "25px", height: "25px" }} />
                </button>
              </div>
              <div className="modal-body">
                {previewEmbed ? (
                  <div className="position-relative" style={{ width: '100%', minHeight: 360 }}>
                    <div className="admin-embed-iframe" dangerouslySetInnerHTML={{ __html: previewEmbed }} />
                  </div>
                ) : (
                  previewOptions?.sources?.[0]?.src ? (
                    <>
                    <div className="adminVideoPlayer playerHeight vjs-16-9">
                      <AdminMiniPlayer id={'ugc-preview-player'} url={previewOptions?.sources?.[0]?.src} videoFormat={previewOptions?.sources?.[0]?.type} title={previewRow?.title} poster={previewRow?.player_image_URL} className="w-100" />
                    </div>
                    <AdminCustomURLCopy link={previewOptions?.sources?.[0]?.src} fieldName={""} className="copy-board-input" />
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <MessageBox text="No preview available for this item." classname="theme-text-color" />
                    </div>
                  )
                )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-secondary" onClick={() => { setPreviewOpen(false); setPreviewRow(null); setPreviewEmbed(null); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modelOpen && (
        <AdminDeleteModal
          messageClassName={deleteActionType === 1 ? 'warningColor' : ''}
          headingText={deleteActionType === 3 ? 'Confirm Undo' : deleteActionType === 1 ? 'Confirm Permanent Delete' : 'Confirm Delete'}
          message={deleteActionType === 2 ? 'Are you sure you want to delete' : deleteActionType === 3 ? 'Are you sure you want to Undo item' : deleteActionType === 1 ? 'Warning: This action will permanently delete the selected items from the server. Once deleted, the data cannot be recovered. Are you sure you want to proceed?' : 'Are you sure you want to Delete'}
          modelClose={modelOpen}
          deleteClose={closeModal}
          deleteAction={handleConfirmDelete}
          buttonText={'custom'}
          actionText={deleteActionType === 1 ? 'Delete Permanent' : deleteActionType === 2 ? 'Delete' : deleteActionType === 3 ? 'Undo' : ''}
        />
      )}

      <AdminNavbar heading={'UGC Management'} totalDataLength={total?.totalItems} deleteCount={0} navText={null} />

      <div className='theme-bg-color p-3 rounded-3 mb-5'>
        <div className='d-flex align-items-center flex-wrap justify-content-between mb-3'>
          <div className='d-flex align-items-center gap-2'>
            <label htmlFor='ugc-status-filter' className='theme-text-color'>Filter</label>
            <select id='ugc-status-filter' className='form-select w-auto' value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }}>
              <option value=''>All</option>
              <option value='0'>Pending</option>
              <option value='1'>Approved</option>
              <option value='2'>Rejected</option>
            </select>
          </div>

          <div className='d-flex align-items-center justify-content-end'>
            {(draft !== 1 && itemToDelete?.length > 0) ? (
              <AdminCustomButton saveText='Delete' className={'warningColorBg me-2'} saveMethod={() => { setModelOpen(true); setDeleteActionType(2) }} />
            ) : ''}
            {(draft === 1 && itemToDelete?.length > 0) ? (
              <AdminCustomButton saveText='Undo' className={'mx-3'} saveMethod={() => { setModelOpen(true); setDeleteActionType(3) }} />
            ) : ''}
            {(draft === 1 && itemToDelete?.length > 0) ? (
              <AdminCustomButton saveText='Delete Permanent' className={'me-3 warningColorBg'} saveMethod={() => { setModelOpen(true); setDeleteActionType(1) }} />
            ) : ''}

            <button id='recycleButton' className={`reloadButton custom-tool-tip-parent p-2 me-2  border border-2 rounded-circle theme-border-color ${draft ? 'button-bg-color theme-button-text-color' : ' bg-transparent theme-text-color'}`} onClick={reCycleBin}>
              <BiRecycle style={{ width: '18px', height: '15px' }} />
              <AdminTooltip text='Recycle Bin' top='-41px' className='d-none custom-tool-tip' />
            </button>

            <button id='reloadButton' className={`bg-transparent custom-tool-tip-parent reloadButton theme-text-color p-2 me-2  border border-2 rounded-circle theme-border-color `} onClick={reloadAction}>
              <RxReload style={{ width: '18px', height: '15px' }} className={`${loading ? 'rotate-load' : ''}`} />
              <AdminTooltip text='Refresh' top='-41px' className='d-none custom-tool-tip' />
            </button>

            <div className='dropdown d-block ms-2' key={'2'}>
              <button className='btn btn-success dropdown-toggle d-flex hideArrow' type='button' id='exportButton' data-bs-toggle='dropdown' aria-expanded='false'>
                <span className='fw-bolder text-white'>Export</span>
                <img src={ExportIcon} className='ms-2 exportIcon' alt='exportIcon' />
              </button>
              <ul className='dropdown-menu theme-bg-color' aria-labelledby='exportButton'>
                <li id='exportCSV' className='dropdown-item theme-text-color' onClick={() => fetchAllData('CSV')}>CSV</li>
                <li id='exportPDF' className='dropdown-item theme-text-color' onClick={() => fetchAllData('PDF')}>PDF</li>
                <li id='exportXLSX' className='dropdown-item theme-text-color' onClick={() => fetchAllData('XLSX')}>XLSX </li>
              </ul>
            </div>

            <div className='dropdown d-block ms-2' key={'3'}>
              <button className='btn btn-success dropdown-toggle d-flex hideArrow' type='button' id='exportAllButton' data-bs-toggle='dropdown' aria-expanded='false'>
                <span className='fw-bolder text-white'>Export All</span>
                <img src={ExportIcon} className='ms-2 exportIcon' alt='exportIcon' />
              </button>
              <ul className='dropdown-menu theme-bg-color' aria-labelledby='exportAllButton'>
                <li id='exportallCSV' className='dropdown-item theme-text-color' onClick={() => fetchAllData('CSV', 'ALL')}>CSV</li>
                <li id='exportallPDF' className='dropdown-item theme-text-color' onClick={() => fetchAllData('PDF', 'ALL')}>PDF</li>
                <li id='exportallXLSX' className='dropdown-item theme-text-color' onClick={() => fetchAllData('XLSX', 'ALL')}>XLSX </li>
              </ul>
            </div>

          </div>
        </div>

        <input type='search' id='ugc-search-input' placeholder='Search' name='title' className='rounded-3 col-3 theme-border-color border-solid-width border-solid table-search  theme-bg-color-secondary mb-3' onChange={handleInput} />

        <AdminTable
          data={pagedRows}
          loading={loading}
          options={options}
          handlePageChange={handlePageChange}
          page={page}
          pagination={total}
          handleSizePerPageChange={handleSizePerPageChange}
          columns={columns}
          limit={limit}
          selectRow={{ selected: itemToDelete }}
          multiSelect
          onRowSelect={handleRowSelect}
          onSort={handleSort}
          onSelectAll={handleSelectAll}
          viewType={'list'}
        />
      </div>
    </div>
  )
}
